import { gql } from '@apollo/client';

// User queries
export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
      isEnabled
      loginDisabled
    }
  }
`;

export const GET_USER = gql`
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      firstName
      lastName
      email
      role
    }
  }
`;

export const LIST_USERS = gql`
  query ListUsers($limit: Int, $nextToken: String) {
    listUsers(limit: $limit, nextToken: $nextToken) {
      items {
        id
        firstName
        lastName
        email
        role
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      email
      role
    }
  }
`;

export const LIST_CLIENTS = gql`
  query ListClients {
    listClients {
      id
      firstName
      lastName
      email
      role
      phoneNumber
      isProspect
    }
  }
`;

// Case queries
export const LIST_CASES = gql`
  query ListCases($limit: Int, $nextToken: String) {
    listCases(limit: $limit, nextToken: $nextToken) {
      items {
        id
        caseNumber
        title
        description
        status
        priority
        clientId
        assignedStaffId
        createdAt
        updatedAt
        client {
          id
          firstName
          lastName
          email
        }
        assignedStaff {
          id
          firstName
          lastName
          role
        }
      }
      nextToken
    }
  }
`;

export const GET_CASE = gql`
  query GetCase($id: ID!) {
    getCase(id: $id) {
      id
      caseNumber
      title
      description
      status
      priority
      clientId
      assignedStaffId
      createdAt
      updatedAt
      client {
        id
        firstName
        lastName
        email
        phoneNumber
      }
      assignedStaff {
        id
        firstName
        lastName
        role
        email
      }
    }
  }
`;

// Inquiry queries
export const GET_INQUIRIES = gql`
  query GetInquiries($limit: Int, $nextToken: String, $status: InquiryStatus) {
    listInquiries(limit: $limit, nextToken: $nextToken, status: $status) {
      items {
        id
        firstName
        lastName
        email
        phoneNumber
        incidentType
        incidentDate
        incidentLocation
        description
        injuryTypes
        injurySeverity
        source
        status
        leadScore
        assignedTo
        assignedToName
        userId
        caseId
        createdAt
        updatedAt
        activityLog {
          id
          activityType
          performedByName
          timestamp
          details
        }
      }
      nextToken
    }
  }
`;

export const GET_INQUIRY = gql`
  query GetInquiry($id: ID!) {
    getInquiry(id: $id) {
      id
      firstName
      lastName
      email
      phoneNumber
      preferredContactMethod
      bestTimeToContact
      zipCode
      address {
        street1
        street2
        city
        state
        zipCode
        country
      }
      incidentType
      incidentDate
      incidentTime
      incidentLocation
      weatherConditions
      description
      injuryTypes
      injurySeverity
      medicalAttentionSought
      medicalAttentionTiming
      treatmentLocation
      ongoingTreatment
      hasHealthInsurance
      policeReportFiled
      policeReportNumber
      atFault
      otherPartyInsured
      otherPartyInsurance
      contactedByInsurance
      hasAttorney
      claimOrLawsuitFiled
      estimatedDamages
      hasPhotosVideos
      hasWitnesses
      witnessInfo
      referralSource
      additionalComments
      
      # Email-specific fields
      source
      referralLink
      emailS3Key
      emailMessageId
      referralTransactionNumber
      brokerCaseManager
      brokerCaseManagerEmail
      dateOfBirth
      spokenLanguage
      employer
      emergencyContactName
      emergencyContactPhone
      documentsUploaded
      crossStreets
      injuryDescription
      treatmentDescription
      lawsuitFiledDate
      tdRequestReason
      isPostConversion
      extractionMetadata
      
      status
      leadScore
      assignedTo
      assignedToName
      assignedAt
      assignedCaseManagerId
      assignedCaseManager {
        id
        firstName
        lastName
        email
      }
      userId
      caseId
      lastContactedAt
      lastContactMethod
      clientLastRespondedAt
      followUpDate
      notes
      createdAt
      updatedAt
      activityLog {
        id
        activityType
        performedBy
        performedByName
        timestamp
        details
        metadata
      }
      attachments {
        id
        fileName
        fileType
        fileSize
        s3Key
        uploadedAt
        description
      }
    }
  }
`;

// Document queries
export const LIST_DOCUMENTS = gql`
  query ListDocuments($limit: Int, $nextToken: String) {
    listDocuments(limit: $limit, nextToken: $nextToken) {
      items {
        id
        filename
        fileSize
        mimeType
        caseId
        clientId
        uploadedBy
        createdAt
        updatedAt
        case {
          id
          caseNumber
          title
        }
        client {
          id
          firstName
          lastName
        }
        uploader {
          id
          firstName
          lastName
        }
      }
      nextToken
    }
  }
`;

// Task queries
export const LIST_TASKS = gql`
  query ListTasks($limit: Int, $nextToken: String) {
    listTasks(limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        status
        priority
        dueDate
        assignedTo
        caseId
        createdBy
        createdAt
        updatedAt
        assignee {
          id
          firstName
          lastName
        }
        case {
          id
          caseNumber
          title
        }
        creator {
          id
          firstName
          lastName
        }
      }
      nextToken
    }
  }
`;

export const LIST_MY_TASKS = gql`
  query ListMyTasks($limit: Int, $nextToken: String) {
    myTasks(limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        status
        priority
        dueDate
        assignedTo
        caseId
        createdBy
        createdAt
        updatedAt
        case {
          id
          caseNumber
          title
        }
        creator {
          id
          firstName
          lastName
        }
      }
      nextToken
    }
  }
`;

// Appointment queries
export const LIST_APPOINTMENTS = gql`
  query ListAppointments($limit: Int, $nextToken: String) {
    listAppointments(limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        startTime
        endTime
        location
        type
        status
        clientId
        staffId
        caseId
        createdAt
        updatedAt
        client {
          id
          firstName
          lastName
          email
        }
        staff {
          id
          firstName
          lastName
        }
        case {
          id
          caseNumber
          title
        }
      }
      nextToken
    }
  }
`;

export const LIST_MY_APPOINTMENTS = gql`
  query ListMyAppointments($limit: Int, $nextToken: String) {
    myAppointments(limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        startTime
        endTime
        location
        type
        status
        clientId
        staffId
        caseId
        createdAt
        updatedAt
        client {
          id
          firstName
          lastName
          email
        }
        case {
          id
          caseNumber
          title
        }
      }
      nextToken
    }
  }
`;

// Statistics queries
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats {
    dashboardStats {
      totalCases
      activeCases
      totalInquiries
      newInquiries
      totalClients
      totalTasks
      overdueTasks
      upcomingAppointments
    }
  }
`;


export const GET_INQUIRY_EMAIL_URL = gql`
  query GetInquiryEmailUrl($inquiryId: ID!) {
    getInquiryEmailUrl(inquiryId: $inquiryId)
  }
`;


// Inquiry Mutations
export const UPDATE_INQUIRY_STATUS = gql`
  mutation UpdateInquiryStatus($id: ID!, $status: InquiryStatus!) {
    updateInquiryStatus(id: $id, status: $status) {
      id
      status
      updatedAt
    }
  }
`;

export const CONVERT_INQUIRY_TO_CASE = gql`
  mutation ConvertInquiryToCase($id: ID!, $assignedAttorneyId: ID, $assignedCaseManagerId: ID) {
    convertInquiryToCase(id: $id, assignedAttorneyId: $assignedAttorneyId, assignedCaseManagerId: $assignedCaseManagerId) {
      id
      caseNumber
      status
    }
  }
`;

export const LOG_INQUIRY_ACTIVITY = gql`
  mutation LogInquiryActivity($input: LogInquiryActivityInput!) {
    logInquiryActivity(input: $input) {
      id
      activityType
      timestamp
      details
    }
  }
`;

export const UPDATE_INQUIRY_TRACKING = gql`
  mutation UpdateInquiryTracking($id: ID!, $input: UpdateInquiryTrackingInput!) {
    updateInquiryTracking(id: $id, input: $input) {
      id
      lastContactedAt
      lastContactMethod
      followUpDate
      notes
      updatedAt
    }
  }
`;


// Status Change with Contact Info Mutation (creates user account for email inquiries)
export const UPDATE_INQUIRY_STATUS_WITH_CONTACT_INFO = gql`
  mutation UpdateInquiryStatusWithContactInfo(
    $id: ID!
    $status: InquiryStatus!
    $editedContactInfo: EditedContactInfoInput
  ) {
    updateInquiryStatusWithContactInfo(
      id: $id
      status: $status
      editedContactInfo: $editedContactInfo
    ) {
      id
      status
      email
      firstName
      lastName
      phoneNumber
      userId
      caseId
      updatedAt
    }
  }
`;

// Reject Inquiry Mutation (with optional account disable)
export const REJECT_INQUIRY = gql`
  mutation RejectInquiry($inquiryId: ID!, $disableAccount: Boolean!) {
    rejectInquiry(inquiryId: $inquiryId, disableAccount: $disableAccount) {
      id
      status
      userId
      updatedAt
    }
  }
`;

// App Configuration Query
export const GET_APP_CONFIG = gql`
  query GetAppConfig {
    getAppConfig {
      googlePlacesApiKey
    }
  }
`;


// Video Conference queries
export const LIST_VIDEO_CONFERENCES = gql`
  query ListVideoConferences($status: ConferenceStatus, $limit: Int, $nextToken: String) {
    listVideoConferences(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        status
        scheduledStartTime
        scheduledEndTime
        actualStartTime
        actualEndTime
        duration
        hostId
        host {
          id
          firstName
          lastName
        }
        clientId
        client {
          id
          firstName
          lastName
        }
        inquiryId
        inquiry {
          id
          firstName
          lastName
        }
        guestName
        guestEmail
        guestPhone
        isRecordingEnabled
        recordingStatus
        waitingRoomParticipants {
          id
          name
          participantType
          requestedAt
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_VIDEO_CONFERENCE = gql`
  query GetVideoConference($id: ID!) {
    getVideoConference(id: $id) {
      id
      title
      description
      status
      scheduledStartTime
      scheduledEndTime
      actualStartTime
      actualEndTime
      duration
      hostId
      host {
        id
        firstName
        lastName
        email
      }
      clientId
      client {
        id
        firstName
        lastName
        email
      }
      inquiryId
      inquiry {
        id
        firstName
        lastName
        email
      }
      caseId
      guestName
      guestEmail
      guestPhone
      joinUrl
      isRecordingEnabled
      recordingStatus
      attendeeIds
      attendees {
        id
        firstName
        lastName
      }
      participants {
        id
        type
        referenceId
        name
        email
        phone
        notifyEmail
        notifyPush
        notifySms
      }
      waitingRoomEnabled
      reminderTimes
      createdAt
      updatedAt
    }
  }
`;

export const LIST_VIDEO_CONFERENCES_BY_INQUIRY = gql`
  query ListVideoConferencesByInquiry($inquiryId: ID!) {
    listVideoConferencesByInquiry(inquiryId: $inquiryId) {
      id
      title
      status
      scheduledStartTime
      scheduledEndTime
      actualStartTime
      actualEndTime
      duration
      hostId
      host {
        id
        firstName
        lastName
      }
      guestName
      guestEmail
      guestPhone
      isRecordingEnabled
      recordingStatus
      waitingRoomParticipants {
        id
        name
        participantType
        requestedAt
      }
      joinUrl
      createdAt
      updatedAt
    }
  }
`;

export const LIST_VIDEO_CONFERENCES_BY_CASE = gql`
  query ListVideoConferencesByCase($caseId: ID!) {
    listVideoConferencesByCase(caseId: $caseId) {
      id
      title
      status
      scheduledStartTime
      scheduledEndTime
      actualStartTime
      actualEndTime
      duration
      hostId
      host {
        id
        firstName
        lastName
      }
      clientId
      client {
        id
        firstName
        lastName
      }
      guestName
      guestEmail
      guestPhone
      isRecordingEnabled
      recordingStatus
      waitingRoomParticipants {
        id
        name
        participantType
        requestedAt
      }
      joinUrl
      createdAt
      updatedAt
    }
  }
`;


// Video Conference Subscriptions
export const ON_VIDEO_CONFERENCE_UPDATED = gql`
  subscription OnVideoConferenceUpdated($conferenceId: ID!) {
    onVideoConferenceUpdated(conferenceId: $conferenceId) {
      id
      title
      status
      scheduledStartTime
      scheduledEndTime
      actualStartTime
      actualEndTime
      duration
      hostId
      host {
        id
        firstName
        lastName
      }
      clientId
      client {
        id
        firstName
        lastName
      }
      inquiryId
      inquiry {
        id
        firstName
        lastName
      }
      guestName
      guestEmail
      guestPhone
      isRecordingEnabled
      recordingStatus
      createdAt
      updatedAt
    }
  }
`;
